import React from 'react';
import { LoginForm, AuthMethodSelect } from 'shared-portal-component';
import { useNavigate } from '@tanstack/react-router';
import { useSelector } from 'react-redux';

import { loginThunk } from '@/store/thunks/auth/loginThunk';
import { forgotPasswordThunk } from '@/store/thunks/auth/forgotPasswordThunk';
import { useAppDispatch } from '@/store';
import { select2FAMethodThunk } from '@/store/thunks/auth/select2FAMethodThunk';
import type { TwoFactorMethod } from '@/types/authState.types';
import { verify2faCodeThunk } from '@/store/thunks/auth/verify2faCodeThunk';
import { resend2faCodeThunk } from '@/store/thunks/auth/resend2faCodeThunk';
import AuthCode from '@/components/molecules/Login/AuthCode';
import EmailOnlyMethodSelect from '@/components/molecules/Login/EmailOnlyMethodSelect';
import { isPhoneVerificationBypassToken } from '@/utils/phoneVerificationBypass';
import {
  selectAuthError,
  selectAuthStatus,
  selectFieldError,
  selectLoginStep,
  selectMaskedEmail,
  selectMaskedPhone,
  selectRegToken,
  selectTwoFactorMethod,
} from '@/store/selectors/auth';
import { setLoginStep, sessionExpired } from '@/store/slices/auth/authSlice';
import { getJwtClaim } from '@/utils/jwtDecoder';

const MS_PER_SECOND = 1000;

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // 🔑 get values directly from the store
  const step = useSelector(selectLoginStep);
  const status = useSelector(selectAuthStatus);
  const twoFactorMethod = useSelector(selectTwoFactorMethod);
  const fieldErrors = useSelector(selectFieldError('code'));
  const loading = status === 'loading';
  const error = useSelector(selectAuthError);
  const maskedEmail = useSelector(selectMaskedEmail);
  const maskedPhone = useSelector(selectMaskedPhone);
  const regToken = useSelector(selectRegToken);

  // Boot the user back to login the moment the two-factor token expires,
  // so an idle 2FA screen can't sit on a dead token. The timeout is derived
  // from the token's own `exp` claim — never a hardcoded TTL.
  React.useEffect(() => {
    if (step !== 'choose2fa' && step !== 'verify2fa') return;
    if (regToken === undefined) return;

    const expSeconds = getJwtClaim<'exp', number>(regToken, 'exp');
    if (typeof expSeconds !== 'number') return;

    const expire = (): void => {
      dispatch(sessionExpired());
      void navigate({ to: '/login' });
    };

    const msUntilExpiry = expSeconds * MS_PER_SECOND - Date.now();
    if (msUntilExpiry <= 0) {
      expire();
      return;
    }

    const timeoutId = setTimeout(expire, msUntilExpiry);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [step, regToken, dispatch, navigate]);

  // Handles login form submit
  const handleLogin = (values: { username: string; password: string }) => {
    void (async () => {
      const action = await dispatch(
        loginThunk({ username: values.username, password: values.password })
      );
      // Admin-forced reset: route into the recovery flow with the email step
      // bypassed. Send the recovery code for the known email, then jump to code entry.
      const payload: unknown = loginThunk.rejected.match(action) ? action.payload : undefined;
      if (
        typeof payload === 'object' &&
        payload !== null &&
        'credentialsResetRequired' in payload &&
        payload.credentialsResetRequired === true
      ) {
        await dispatch(forgotPasswordThunk({ emailAddress: values.username }));
        await navigate({ to: '/reset-password/forced' });
      }
    })();
  };

  const handleForgotPassword = () => {
    void dispatch(select2FAMethodThunk({ method: twoFactorMethod }));
  };

  // Handles selection of method
  const handleSelectMethod = (method: TwoFactorMethod) => {
    void dispatch(select2FAMethodThunk({ method }));
  };

  // Handles code input submit
  const handleCodeSubmit = (code: string) => {
    void dispatch(verify2faCodeThunk({ code }))
      .unwrap()
      .then(() => {
        void navigate({ to: '/admin' }); // ✅ redirect after success
      });
  };

  const handleBackToMethod = () => {
    dispatch(setLoginStep('choose2fa'));
  };

  const handleResend = () => {
    void dispatch(resend2faCodeThunk({ method: twoFactorMethod }));
  };

  // ---- RENDER LOGIC ----

  let content = null;
  switch (step) {
    case 'credentials':
      content = (
        <LoginForm
          onSubmit={handleLogin}
          onForgotPassword={handleForgotPassword}
          loading={loading}
          error={error}
        />
      );
      break;
    case 'choose2fa':
      content = isPhoneVerificationBypassToken(regToken) ? (
        <EmailOnlyMethodSelect onSelect={handleSelectMethod} email={maskedEmail ?? ''} />
      ) : (
        <AuthMethodSelect
          onSelect={handleSelectMethod}
          email={maskedEmail ?? ''}
          phone={maskedPhone ?? ''}
        />
      );
      break;
    case 'verify2fa':
      content = (
        <AuthCode
          method={twoFactorMethod}
          onBack={handleBackToMethod}
          onResend={handleResend}
          onSubmit={handleCodeSubmit}
          loading={loading}
          error={fieldErrors}
          masked={twoFactorMethod === 'email' ? (maskedEmail ?? '') : (maskedPhone ?? '')}
        />
      );
      break;
  }

  return <>{content}</>;
};

export default Login;
